import styled, { keyframes } from 'styled-components';
import { media } from '@/styles/Qtheme.ts';
import { MobileCalVW, TabletCalVW } from '@utils/CalVW.tsx';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

export const AssetOverviewSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 200px;
  margin-top: 50px;
  width: 1200px;
  animation: ${fadeInUp} 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  @media ${media.desktop} {
    width: ${TabletCalVW(900)};
  }

  @media ${media.mobile} {
    margin-bottom: 150px;
    margin-top: 30px;
  }
`;

export const AssetOverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 30px;
  gap: 32px;
  position: relative;

  > * {
    animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    
    &:nth-child(1) { animation-delay: 0.1s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.3s; }
    &:nth-child(4) { animation-delay: 0.4s; }
  }

  @media ${media.mobile} {
    gap: 24px;
  }
`;

export const AssetOverviewTotal = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  @media only screen and (max-width: 1200px) {
    width: 100%;
    height: 50%;
  }
`;
export const AssetOverviewTotalValueSection = styled.div`
  width: 100%;
  padding: 40px 0;
  display: flex;
  background: ${({theme}) => theme.itemBg};
  border-radius: 20px;
  border: 1px solid ${({theme}) => theme.COLORS.border || 'rgba(255, 255, 255, 0.1)'};
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(248, 121, 67, 0.04) 0%,
        rgba(255, 76, 0, 0.02) 40%,
        transparent 70%
      );
      transform: translate(-50%, -50%);
      pointer-events: none;
      animation: ${floatAnimation} 6s ease-in-out infinite;
    }

    &:hover {
      transform: translateY(-4px) scale(1.01);
      box-shadow: 
          0 16px 48px rgba(248, 121, 67, 0.25),
          0 6px 16px rgba(255, 76, 0, 0.18),
          inset 0 1px 0 rgba(255, 255, 255, 0.25),
          0 0 0 1px rgba(248, 121, 67, 0.2),
          0 0 20px rgba(255, 76, 0, 0.1);
    }

    @media ${media.mobile} {
      flex-direction: column;
      padding: 28px 24px;
      border-radius: 16px;    
    }
    
    &:hover {
      transform: translateY(-2px) scale(1.005);
    }
`;

export const MyAssetSection = styled.div`
  width: 100%;
  height: 100%;
  margin-top: 20px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.itemBg};
  border-radius: 18px;
  border: 1px solid ${({theme}) => theme.COLORS.border || 'rgba(255, 255, 255, 0.1)'};
  box-shadow: 
    0 6px 24px rgba(0, 0, 0, 0.11),
    0 2px 6px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
        90deg,
        transparent 0%,
        ${({theme}) => theme.COLORS.border || 'rgba(255, 255, 255, 0.2)'} 50%,
        transparent 100%
    );
    opacity: 0.3;
  }

  &:hover {
    transform: translateY(-3px) scale(1.005);
    box-shadow: 
      0 12px 40px rgba(248, 121, 67, 0.2),
      0 6px 12px rgba(255, 76, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.18),
      0 0 0 1px rgba(248, 121, 67, 0.12),
      0 0 15px rgba(255, 76, 0, 0.08);
  }

  @media ${media.desktop} {
    min-height: 600px;
    padding-bottom: 20px;
  }

  @media ${media.mobile} {
    &:hover {
      transform: translateY(-2px) scale(1.002);
    }
  }
`;

export const RecentDepositSection = styled.div`
  width: 100%;
  padding: 40px;
  background: ${({theme}) => theme.itemBg};
  border-radius: 18px;
  border: 1px solid ${({theme}) => theme.COLORS.border || 'rgba(255, 255, 255, 0.1)'};
  box-shadow: 
    0 6px 24px rgba(0, 0, 0, 0.11),
    0 2px 6px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${({theme}) => theme.COLORS.border || 'rgba(255, 255, 255, 0.2)'} 50%,
      transparent 100%
    );
    opacity: 0.3;
  }

  &:hover {
    transform: translateY(-3px) scale(1.005);
    box-shadow: 
      0 12px 40px rgba(248, 121, 67, 0.18),
      0 6px 12px rgba(255, 76, 0, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.18),
      0 0 0 1px rgba(248, 121, 67, 0.1),
      0 0 15px rgba(255, 76, 0, 0.06);
  }

  @media ${media.mobile} {
    padding: 28px 24px;
    border-radius: 14px;
        
    &:hover {
      transform: translateY(-2px) scale(1.002);
    }
  }
`;

export const RecentDepositTableSection = styled.div`
  padding-inline: 10px;
  overflow-x: hidden;
  overflow-y: auto;

  width: 100%;
`;
